import { useMutation } from "@tanstack/react-query"
import { Form, Input, Button, message, Space, Row, Col } from "antd"
import { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  AuthActions,
  AuthContext,
  AuthForms,
} from "../../contexts/auth/AuthContext"
import { LOGIN, REGISTER, VERIFY_2FA } from "./mutations"

const SignInForm = () => {
  const { t } = useTranslation()
  const [isRegistering, setIsRegistering] = useState(false)
  // 2FA: kod so'ralayotgan email; null = oddiy login rejimi.
  // Avval webda bu bosqich umuman yo'q edi — 2FA yoqilgan foydalanuvchi
  // webdan kira olmasdi (auditda qayd etilgan kamchilik).
  const [twoFactorEmail, setTwoFactorEmail] = useState<string | null>(null)
  const { authState, authDispatch } = useContext(AuthContext)

  const applyLoginResult = (data: any) => {
    if (data?.result?.accessToken) {
      authDispatch({
        type: AuthActions.SIGN_IN,
        payload: {
          user: data?.result?.user,
          accessToken: data?.result?.accessToken,
          refreshToken: data?.result?.refreshToken,
          warehouses: data?.result?.warehouses,
        },
      })
      message.success(t("welcomeBack"))
    }
  }

  const {
    mutate: login,
    isLoading: loading,
    error,
    data,
  } = useMutation(LOGIN, {
    onSuccess: (data: any, variables) => {
      // Server 2FA talab qildi — kod kiritish bosqichiga o'tamiz
      if (data?.twoFactorRequired) {
        setTwoFactorEmail(variables?.email || data?.email || "")
        message.info(data?.message || t("codeSentTelegram"))
        return
      }
      applyLoginResult(data)
    },
    onError: (error: any, variables) => {
      const serverMsg = error?.response?.data?.message || error?.message || t("invalidCredentials")
      // Eski backend 2FA'ni 400 + "2FA_REQUIRED" bilan qaytarardi — deploy oralig'ida
      // ham ishlashi uchun shu shakl ham qabul qilinadi
      if (String(serverMsg).includes("2FA")) {
        setTwoFactorEmail(variables?.email || "")
        message.info(t("codeSentTelegram"))
        return
      }
      message.error(serverMsg)
    },
  })

  const { mutate: verifyCode, isLoading: verifying } = useMutation(VERIFY_2FA, {
    onSuccess: (data: any) => {
      if (data?.result?.accessToken) {
        applyLoginResult(data)
        setTwoFactorEmail(null)
      } else {
        message.error(data?.message || t("invalidConfirmCode"))
      }
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || t("invalidConfirmCode"))
    },
  })
  const {
    mutate: register,
    isLoading: rLoading,
    error: rError,
  } = useMutation(REGISTER, {
    onSuccess: (data) => {
      if (data) {
        message.success(t("registered"))
        setIsRegistering(false)
      } else if (!data) {
        message.error(t("error"))
      }
    },
    onError: (error) => {
      message.error(t("error"))
    },
  })

  async function onFinish(values: any) {
    if (!values) return

    if (isRegistering) {
      try {
        await register({
          lastname: values.lastname,
          name: values.name,
          middlename: values.middlename,
          address: values.address,
          phone: values.phone,
          email: values.email,
          password: values.password,
          confirmPassword: values.confirmPassword,
        })
      } catch (error: any) {
        message.error(error.toString())
      }
      return
    }

    if (authState.currentForm === AuthForms.FORGOT_PASSWORD) {
      // 2026-09-29 (menejer qo'llanmasi auditi): serverga so'rov YO'Q edi, lekin «Новый пароль отправлен» deb aldardi —
      // parolni faqat administrator tiklaydi («Пользователи» / «Менеджеры» → «Сменить пароль»), shuni aytamiz
      message.info(t("forgotPasswordAskAdmin", "Пароль восстанавливает администратор системы — обратитесь к нему"), 6)
      authDispatch({ type: AuthActions.TOGGLE_FORM })
      return
    }

    if (!isRegistering) {
      try {
        await login({
          email: values.email,
          password: values.password,
        })
      } catch (err: any) {
        message.error(err.toString())
      }
    }
  }

  // 2FA bosqichi: alohida mini-forma (email/parol qayta so'ralmaydi)
  if (twoFactorEmail !== null) {
    return (
      <Form
        name="two-factor-form"
        layout="vertical"
        style={{ width: "100%" }}
        onFinish={(v: any) => verifyCode({ email: twoFactorEmail, code: String(v.code || "").trim() })}
      >
        <Form.Item
          name="code"
          label={t("confirmCodeTelegram")}
          rules={[{ required: true, message: t("enterTelegramCode") }]}
        >
          <Input size="large" autoFocus maxLength={6} inputMode="numeric" placeholder="123456" />
        </Form.Item>
        <Space style={{ justifyContent: "space-between", width: "100%", marginBottom: 24 }}>
          <Button style={{ paddingLeft: 0 }} type="link" onClick={() => setTwoFactorEmail(null)}>
            {t("signIn")}
          </Button>
        </Space>
        <Button block type="primary" htmlType="submit" loading={verifying} className="login-form-button">
          {t("send")}
        </Button>
      </Form>
    )
  }

  return (
    <Form
      name="sign-in-form"
      className="login-form"
      initialValues={{ remember: true }}
      layout="vertical"
      style={{ width: "100%" }}
      onFinish={onFinish}
    >
      {!isRegistering ? (
        <>
          <Form.Item
            name="email"
            label={t("email")}
            rules={[{ required: true, message: t("emailPlaceholder") }]}
          >
            <Input
              size="large"
              autoComplete="email"
              type="email"
              placeholder={t("emailPlaceholder")}
            />
          </Form.Item>

          {authState.currentForm !== AuthForms.FORGOT_PASSWORD && (
            <Form.Item
              name="password"
              label={t("password")}
              rules={[{ required: true, message: t("passwordPlaceholder") }]}
            >
              <Input.Password
                size="large"
                autoComplete="currentPassword"
                type="password"
                placeholder={t("passwordPlaceholder")}
              />
            </Form.Item>
          )}
        </>
      ) : (
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="name"
              label={t("name")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="text"
                placeholder={t("name")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="middlename"
              label={t("middlename")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="text"
                placeholder={t("middlename")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="lastname"
              label={t("lastname")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="text"
                placeholder={t("lastname")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label={t("email")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="email"
                addonAfter="@"
                placeholder={t("email")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="address"
              label={t("address")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="address"
                placeholder={t("address")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="phone"
              label={t("phone")}
              rules={[{ required: true }]}
            >
              <Input
                style={{ width: "100%" }}
                size="large"
                type="text"
                placeholder={t("phone")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item name="password" label={t("password")}>
              <Input.Password
                style={{ width: "100%" }}
                size="large"
                placeholder={t("password")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="confirmPassword" label={t("confirmPassword")}>
              <Input.Password
                style={{ width: "100%" }}
                size="large"
                placeholder={t("confirmPassword")}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Space
        style={{
          justifyContent: "space-between",
          width: "100%",
          marginBottom: 24,
        }}
      >
        <Button
          style={{ paddingLeft: 0 }}
          type="link"
          onClick={() => authDispatch({ type: AuthActions.TOGGLE_FORM })}
          className="login-form-forgot"
        >
          {authState.currentForm === AuthForms.FORGOT_PASSWORD
            ? t("rememberedPassword")
            : t("forgotPassword")}
        </Button>
        {/* Boshliq (2026-09-02, audit INV2-03): ochiq ro'yxatdan o'tish YOPILDI — kontragent loginini faqat admin yaratadi
            (server ham faqat adminga ruxsat beradi). Tugma olib tashlandi. */}
        {isRegistering && (
          <Button style={{ paddingLeft: 0 }} type="link" onClick={() => setIsRegistering(false)} className="login-form-forgot">
            {t("signIn")}
          </Button>
        )}
      </Space>

      <Button
        block
        type="primary"
        htmlType="submit"
        loading={loading || rLoading}
        danger={!!error || !!rError}
        className="login-form-button"
      >
        {authState.currentForm === AuthForms.FORGOT_PASSWORD
          ? t("send")
          : !isRegistering
          ? t("signIn")
          : t("register")}
      </Button>
    </Form>
  )
}

export default SignInForm
